import * as React from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Loader2,
  MoreHorizontal,
  Users,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { API } from 'aws-amplify';
import gqlTag from 'graphql-tag';
import Fuse from 'fuse.js';
import getUserAvatar from '../../../application/common/utils/getUserAvatar';
import fetchTeamMembers from '../../../application/routes/teams/fetchTeamMembers';
import fetchTeamLevels from '../../../application/routes/teams/fetchTeamLevels';
import { usePermissions } from '../../../application/context/PermissionsContext/PermissionsContext';
import { PermissionKeys } from '../../../application/context/PermissionsContext/permissionTypes';
import featureFlag from '../../../application/common/featureFlag';
import { PageHeader } from '../../components/page-header';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { useToast } from '../../components/ui/toast';
import { cn } from '../../lib/utils';

const gql: any = gqlTag;

declare const global: {
  me: {
    id: string;
    orgID: string;
    imageName?: string;
    name: string;
    isAdmin?: boolean;
  };
};

interface TeamLevel {
  label: string;
  value: string;
  levelNum: number;
}

interface CircleMember {
  id: string;
  circle: any;
  memberType: string;
  user?: any;
  remainingDaysInCircle?: number;
  daysBeforeStart?: number;
}

export function TeamsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { checkPermission, hasTeamPermission } = usePermissions();

  const canCreate = checkPermission(PermissionKeys.TeamsPeople_CreateTeam);
  const canMultiDelete =
    checkPermission(PermissionKeys.TeamsPeople_DeleteTeam) === 'everyone';

  const [searchString, setSearchString] = useState('');
  const [myTeams, setMyTeams] = useState<CircleMember[]>([]);
  const [teamLevels, setTeamLevels] = useState<string[]>(['all']);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [checkedTeams, setCheckedTeams] = useState<any[]>([]);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const [listFilters, setListFilters] = useState({
    level: 'all',
    role: 'all',
    type: 'all',
  });

  // Fetch team levels on mount
  useEffect(() => {
    async function getTeamLevels() {
      const tempTeamLevels: TeamLevel[] = await fetchTeamLevels();
      const labels = ['all'];
      for (let i = tempTeamLevels.length - 1; i >= 0; i--) {
        labels.push(tempTeamLevels[i].label);
      }
      setTeamLevels(labels);
    }
    getTeamLevels();
  }, []);

  // Fetch teams
  useEffect(() => {
    fetchTeams();
  }, [refetchTrigger]);

  const fetchTeams = async () => {
    setIsLoading(true);
    try {
      const myQuery = gql`
        query myQuery($orgID: ID!, $searchString: String!, $nextToken: String) {
          searchCircles(
            filter: {
              name: { wildcard: $searchString }
              orgID: { eq: $orgID }
              isDeleted: { eq: false }
            }
            limit: 999
            nextToken: $nextToken
            sort: { direction: asc, field: name }
          ) {
            nextToken
            items {
              approverType
              avatarImageName
              description
              endDate
              id
              imageName
              imageType
              inviterType
              isActive
              launchDate
              location
              level {
                id
                name
                levelNum
              }
              name
              scope
              startDate
              status
              parentCircleID
              levelID
              parent {
                id
                name
                parentCircleID
              }
              children {
                items {
                  id
                  name
                  parentCircleID
                }
              }
              users {
                items {
                  id
                  isActive
                  lifetimeCirclePoints
                  memberType
                  status
                  circlePoints
                  orgID
                  circleID
                  user {
                    id
                    functionalRoles
                    status
                  }
                }
              }
            }
            total
          }
        }
      `;

      let teamsList: any[] = [];
      let teamsNextToken: string | null = null;

      do {
        const res: any = await (
          API.graphql({
            query: myQuery,
            variables: {
              orgID: global.me.orgID,
              searchString: '*',
              nextToken: teamsNextToken,
            },
          }) as Promise<any>
        ).catch((err: any) => {
          // Amplify v5 throws on partial errors but still includes data
          if (err?.data?.searchCircles) {
            return { data: err.data };
          }
          throw err;
        });
        teamsList = [...teamsList, ...(res?.data?.searchCircles?.items || [])];
        teamsNextToken = res?.data?.searchCircles?.nextToken;
      } while (teamsNextToken);

      const newCircleMembers: CircleMember[] = [];

      for (const circle of teamsList) {
        circle.avatarFile = await getUserAvatar(
          circle.imageName || '',
          circle.imageType,
          true
        );

        const usersItems = (circle.users?.items || []).filter(
          (item: any) => item != null
        );

        // Fetch all members if at the 100 limit
        let teamMembers;
        if (usersItems.length === 100) {
          teamMembers = await fetchTeamMembers(circle.id);
          teamMembers = (teamMembers || []).filter(
            (member: any) => member.status === 'active'
          );
        }

        if (usersItems.length > 0) {
          const circleMember: any = { ...usersItems[0] };
          circleMember.circle = circle;
          circleMember.memberType = 'member';

          if (teamMembers) {
            circleMember.circle.users = teamMembers;
            for (const cm of teamMembers) {
              if (cm.id === global.me.id) {
                circleMember.memberType = cm.circleMember.memberType;
              }
            }
          } else {
            circleMember.circle.users = usersItems.filter(
              (user: any) =>
                user &&
                user.status !== 'removed' &&
                user.user &&
                user.user?.status === 'active'
            );
            for (const cm of usersItems) {
              if (cm && cm.user?.id === global.me.id) {
                circleMember.memberType = cm.memberType;
              }
            }
          }

          newCircleMembers.push(circleMember);
        } else {
          const circleMember: any = {
            circlePoints: 0,
            id: '',
            isActive: true,
            lifetimeCirclePoints: 0,
            memberType: 'owner',
            orgID: global.me.orgID,
            status: 'NotaMember',
            circle,
          };
          circleMember.circle.users = usersItems.filter(
            (user: any) => user.status !== 'removed'
          );
          newCircleMembers.push(circleMember);
        }
      }

      setMyTeams(newCircleMembers);
    } catch (error) {
      console.error('ERROR FETCHING TEAMS', error);
      toast.error('Failed to load teams');
    } finally {
      setIsLoading(false);
    }
  };

  // Fuse search
  const fuse = useMemo(
    () =>
      new Fuse(myTeams, {
        keys: ['circle.name', 'circle.description'],
        includeScore: true,
        threshold: 0.2,
      }),
    [myTeams]
  );

  // Filtered + searched list
  const filteredTeams = useMemo(() => {
    let list = myTeams;

    if (searchString.trim()) {
      const results = fuse.search(searchString);
      list = results.map((r) => r.item);
    }

    if (listFilters.level !== 'all') {
      list = list.filter(
        (team) => team.circle.level?.name === listFilters.level
      );
    }
    if (listFilters.role !== 'all') {
      list = list.filter((team) => {
        if (team?.user?.functionalRoles) {
          return team.user.functionalRoles.includes(listFilters.role);
        }
        return false;
      });
    }
    if (listFilters.type !== 'all') {
      list = list.filter((team) => team.memberType === listFilters.type);
    }

    return list;
  }, [myTeams, searchString, listFilters, fuse]);

  const totalPages = Math.ceil(filteredTeams.length / limit);
  const paginatedTeams = useMemo(() => {
    const start = (currentPage - 1) * limit;
    return filteredTeams.slice(start, start + limit);
  }, [filteredTeams, currentPage, limit]);

  const handleFilterChange = useCallback((name: string, value: string) => {
    setListFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  }, []);

  const handleTeamCheck = useCallback((team: any, checked: boolean) => {
    if (checked) {
      setCheckedTeams((prev) => [...prev, team]);
    } else {
      setCheckedTeams((prev) => prev.filter((t) => t.id !== team.id));
    }
  }, []);

  const handleDeleteTeams = async (teams: any[]) => {
    if (!teams.length) {
      return;
    }
    try {
      const mutations = await import('../../../graphql/mutations');
      for (const team of teams) {
        await API.graphql({
          query: mutations.updateCircle,
          variables: { input: { id: team.id, isDeleted: true } },
        });
      }
      toast.success(
        teams.length === 1
          ? `${teams[0].name} deleted successfully`
          : `${teams.length} teams deleted successfully`
      );
      setCheckedTeams([]);
      setRefetchTrigger((prev) => prev + 1);
    } catch (err) {
      console.error('onDeleteTeams error:', err);
      toast.error('Failed to delete teams. Please try again.');
    }
  };

  const handleTeamClick = useCallback(
    (teamId: string) => {
      navigate(`/v2/teams/${teamId}`);
    },
    [navigate]
  );

  const getFunctionalRole = (team: CircleMember): string => {
    const rolesArr = team?.user?.functionalRoles;
    if (!rolesArr) {
      return 'None';
    }
    const formatted = rolesArr
      .filter((r: string) => r !== '')
      .map((r: string) => r.charAt(0).toUpperCase() + r.slice(1))
      .join(' & ');
    return formatted || 'None';
  };

  const headerActions = canCreate
    ? [
        {
          id: 'create',
          label: 'New team',
          icon: <Plus className="size-4" />,
          variant: 'primary' as const,
          onClick: () => navigate('/v2/teams/new'),
        },
      ]
    : [];

  return (
    <div className="flex flex-col h-full">
      <PageHeader title="Teams" actions={headerActions} />

      <div className="flex-1 overflow-auto p-4 md:p-6 space-y-4">
        {/* Filters */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Search teams..."
              className="pl-10"
              value={searchString}
              onChange={(e) => {
                setSearchString(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <Select
            value={listFilters.level}
            onValueChange={(v) => handleFilterChange('level', v)}
          >
            <SelectTrigger className="w-full sm:w-[160px]">
              <SelectValue placeholder="Team Level" />
            </SelectTrigger>
            <SelectContent>
              {teamLevels.map((level) => (
                <SelectItem key={level} value={level}>
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={listFilters.role}
            onValueChange={(v) => handleFilterChange('role', v)}
          >
            <SelectTrigger className="w-full sm:w-[140px]">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              {[
                'all',
                'manager',
                ...(featureFlag('noSetterCloser') ? [] : ['setter', 'closer']),
              ].map((role) => (
                <SelectItem key={role} value={role}>
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={listFilters.type}
            onValueChange={(v) => handleFilterChange('type', v)}
          >
            <SelectTrigger className="w-full sm:w-[160px]">
              <SelectValue placeholder="Member Type" />
            </SelectTrigger>
            <SelectContent>
              {['all', 'member', 'admin', 'owner'].map((type) => (
                <SelectItem key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <span className="text-sm text-muted-foreground sm:ml-auto">
            {filteredTeams.length} teams
          </span>
        </div>

        {/* Bulk actions */}
        {canMultiDelete && checkedTeams.length > 0 && (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
            <span className="text-sm font-medium text-destructive">
              {checkedTeams.length} selected
            </span>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleDeleteTeams(checkedTeams)}
            >
              Delete selected
            </Button>
          </div>
        )}

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
          </div>
        ) : filteredTeams.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <Users className="size-12 mb-3" />
            <p className="text-sm">No teams found.</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block rounded-xl border border-border overflow-hidden">
              {/* Header */}
              <div className="grid grid-cols-[2fr_1.5fr_1fr_1fr_1fr_0.8fr_40px] gap-0 bg-muted/50 border-b border-border px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                <span>Name</span>
                <span>Parent Team</span>
                <span>Team Level</span>
                <span>My Role</span>
                <span>Member Type</span>
                <span>Members</span>
                <span />
              </div>

              {/* Rows */}
              <div className="divide-y divide-border">
                {paginatedTeams.map((team) => {
                  const circle = team.circle;
                  const canClick = hasTeamPermission(
                    PermissionKeys.TeamsPeople_ViewTeamDetails,
                    [circle?.id]
                  );
                  const showEdit = hasTeamPermission(
                    PermissionKeys.TeamsPeople_UpdateTeam,
                    [circle?.id]
                  );
                  const showDelete = hasTeamPermission(
                    PermissionKeys.TeamsPeople_DeleteTeam,
                    [circle?.id]
                  );
                  const showAddPerson = hasTeamPermission(
                    PermissionKeys.TeamsPeople_AddPersonToTeam,
                    [circle?.id]
                  );
                  const showMenu = showEdit || showAddPerson || showDelete;

                  return (
                    <div
                      key={circle.id}
                      className={cn(
                        'grid grid-cols-[2fr_1.5fr_1fr_1fr_1fr_0.8fr_40px] gap-0 px-4 py-3 items-center hover:bg-muted/30 transition-colors',
                        canClick && 'cursor-pointer'
                      )}
                      onClick={() => {
                        if (canClick) {
                          handleTeamClick(circle.id);
                        }
                      }}
                    >
                      {/* Name + Avatar */}
                      <div className="flex items-center gap-3 min-w-0">
                        {canMultiDelete && (
                          <input
                            type="checkbox"
                            className="size-4 rounded border-border accent-primary shrink-0"
                            checked={checkedTeams.some(
                              (t) => t.id === circle.id
                            )}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleTeamCheck(circle, e.target.checked);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        {circle.avatarFile ? (
                          <img
                            src={circle.avatarFile}
                            alt={circle.name}
                            className="size-9 rounded-full object-cover shrink-0"
                          />
                        ) : (
                          <div className="size-9 rounded-full bg-muted-foreground/20 flex items-center justify-center shrink-0">
                            <span className="text-xs font-semibold text-foreground">
                              {circle.name?.[0]?.toUpperCase()}
                            </span>
                          </div>
                        )}
                        <span className="text-sm font-medium text-foreground truncate">
                          {circle.name}
                        </span>
                      </div>

                      {/* Parent Team */}
                      <span className="text-sm text-foreground truncate">
                        {circle.parent?.name || 'None'}
                      </span>

                      {/* Team Level */}
                      <span className="text-sm text-foreground truncate">
                        {circle.level?.name
                          ? circle.level.name.charAt(0).toUpperCase() +
                            circle.level.name.slice(1)
                          : 'N/A'}
                      </span>

                      {/* My Role */}
                      <span className="text-sm text-foreground truncate">
                        {getFunctionalRole(team)}
                      </span>

                      {/* Member Type */}
                      <span className="text-sm text-foreground truncate">
                        {team.memberType
                          ? team.memberType.charAt(0).toUpperCase() +
                            team.memberType.slice(1)
                          : 'Member'}
                      </span>

                      {/* Members count */}
                      <span className="text-sm text-foreground">
                        {circle.users?.length || 0}
                      </span>

                      {/* Actions */}
                      <div onClick={(e) => e.stopPropagation()}>
                        {showMenu && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-8"
                              >
                                <MoreHorizontal className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {showEdit && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    navigate(`/v2/teams/edit/${circle.id}`)
                                  }
                                >
                                  Edit
                                </DropdownMenuItem>
                              )}
                              {showAddPerson && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    navigate(`/v2/teams/${circle.id}`)
                                  }
                                >
                                  Add members
                                </DropdownMenuItem>
                              )}
                              {showDelete && (
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onClick={() => handleDeleteTeams([circle])}
                                >
                                  Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {paginatedTeams.map((team) => {
                const circle = team.circle;
                const canClick = hasTeamPermission(
                  PermissionKeys.TeamsPeople_ViewTeamDetails,
                  [circle?.id]
                );

                return (
                  <div
                    key={circle.id}
                    className={cn(
                      'rounded-xl border border-border p-4 space-y-2',
                      canClick && 'cursor-pointer hover:bg-muted/30'
                    )}
                    onClick={() => {
                      if (canClick) {
                        handleTeamClick(circle.id);
                      }
                    }}
                  >
                    <div className="flex items-center gap-3">
                      {circle.avatarFile ? (
                        <img
                          src={circle.avatarFile}
                          alt={circle.name}
                          className="size-10 rounded-full object-cover shrink-0"
                        />
                      ) : (
                        <div className="size-10 rounded-full bg-muted-foreground/20 flex items-center justify-center shrink-0">
                          <span className="text-sm font-semibold text-foreground">
                            {circle.name?.[0]?.toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {circle.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {circle.level?.name || 'No level'} &middot;{' '}
                          {circle.users?.length || 0} members
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>Parent: {circle.parent?.name || 'None'}</span>
                      <span>Type: {team.memberType || 'member'}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-4">
                <Button
                  variant="outline"
                  size="icon"
                  className="size-8"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  <ChevronLeft className="size-4" />
                </Button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <Button
                    key={i}
                    variant={currentPage === i + 1 ? 'default' : 'outline'}
                    size="sm"
                    className="size-8 p-0"
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="icon"
                  className="size-8"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  <ChevronRight className="size-4" />
                </Button>

                <Select
                  value={String(limit)}
                  onValueChange={(v) => {
                    setLimit(Number(v));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-[100px] ml-4">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 25, 50, 100].map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n} / page
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
